import { categorizeDraftError, isBrowserOnline, isRetryableDraftError } from './draft-errors';
import { draftAnswersEqual } from './draft-serializer';
import { AutosaveStatus, CategorizedDraftError, DraftErrorCategory, DraftSnapshot } from './draft.types';

export interface AutosaveState {
  status: AutosaveStatus;
  /** ISO timestamp of the last acknowledged save in this lifecycle. */
  lastSavedAt?: string;
}

export type AutosaveEvent =
  | { type: 'save-success'; attempt: number }
  | { type: 'save-failure'; attempt: number; category: DraftErrorCategory; status?: number }
  | { type: 'retry-scheduled'; attempt: number; delayMs: number }
  | { type: 'paused' }
  | { type: 'stopped'; category: DraftErrorCategory };

export interface AutosaveCoordinatorOptions {
  /** Persists the snapshot. Resolves on server acknowledgement; rejects with the transport error. */
  save: (snapshot: DraftSnapshot) => Promise<void>;
  onEvent: (event: AutosaveEvent) => void;
}

/** Autosave timing (spec FR-03, FR-09). */
export const AUTOSAVE_CONFIG = {
  /** Quiet period after the last edit before saving. */
  debounceMs: 1000,
  /** Longest an edit waits while the patient keeps typing. */
  maxWaitMs: 10000,
  /** Delays between automatic retries; its length is the retry budget (1 attempt + 3 retries). */
  retryDelaysMs: [1000, 2000, 4000],
  /** Max positive jitter as a ratio of the retry delay. */
  jitterRatio: 0.25,
} as const;

type Timer = ReturnType<typeof setTimeout>;

const subscribeConnectivity = (onChange: (online: boolean) => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const handleOnline = () => onChange(true);
  const handleOffline = () => onChange(false);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

/**
 * Framework-free autosave state machine (spec §8.2).
 * - Trailing debounce with a max-wait so continuous typing still saves.
 * - At most one save in flight; edits made during a save coalesce into the next one.
 * - An acknowledgement only advances the baseline to the snapshot that was sent.
 * - Bounded retries with backoff + jitter; offline never consumes the retry budget.
 * - Generation counter so responses that land after pause/dispose are ignored.
 */
export const createAutosaveCoordinator = ({ save, onEvent }: AutosaveCoordinatorOptions) => {
  const { debounceMs, maxWaitMs, retryDelaysMs, jitterRatio } = AUTOSAVE_CONFIG;

  let state: AutosaveState = { status: 'idle' };
  const listeners = new Set<(next: AutosaveState) => void>();

  let baseline: DraftSnapshot | null = null;
  let latest: DraftSnapshot | null = null;
  let generation = 0;
  let inFlight = false;
  /** Retry attempt index for the current failure streak (0 = first attempt). */
  let attempt = 0;
  let suspended = false;
  let stopped = false;
  let disposed = false;

  let debounceTimer: Timer | undefined;
  let maxWaitTimer: Timer | undefined;
  let retryTimer: Timer | undefined;

  const setState = (next: Partial<AutosaveState>) => {
    state = { ...state, ...next };
    listeners.forEach((listener) => listener(state));
  };

  const clearTimer = (timer: Timer | undefined) => {
    if (timer !== undefined) clearTimeout(timer);
  };

  const clearScheduling = () => {
    clearTimer(debounceTimer);
    clearTimer(maxWaitTimer);
    debounceTimer = undefined;
    maxWaitTimer = undefined;
  };

  const clearAllTimers = () => {
    clearScheduling();
    clearTimer(retryTimer);
    retryTimer = undefined;
  };

  const hasUnsavedChanges = () => !!latest && !draftAnswersEqual(latest, baseline);

  const settledStatus = (): AutosaveStatus => (state.lastSavedAt ? 'saved' : 'idle');

  const canSave = () => !disposed && !suspended && !stopped;

  const scheduleSave = () => {
    clearTimer(debounceTimer);
    debounceTimer = setTimeout(flush, debounceMs);
    if (maxWaitTimer === undefined) maxWaitTimer = setTimeout(flush, maxWaitMs);
  };

  const runSave = async () => {
    if (!latest) return;
    const captured = latest;
    const requestGeneration = generation;
    const currentAttempt = attempt;

    inFlight = true;
    setState({ status: currentAttempt > 0 ? 'retrying' : 'saving' });

    try {
      await save(captured);
      if (requestGeneration !== generation) return;

      inFlight = false;
      attempt = 0;
      baseline = captured;
      onEvent({ type: 'save-success', attempt: currentAttempt });

      // Newer edits that arrived while this save was in flight stay dirty.
      const stillDirty = hasUnsavedChanges();
      setState({ status: stillDirty ? 'dirty' : 'saved', lastSavedAt: new Date().toISOString() });
      if (stillDirty) scheduleSave();
    } catch (error) {
      if (requestGeneration !== generation) return;
      inFlight = false;
      handleFailure(categorizeDraftError(error), currentAttempt);
    }
  };

  const handleFailure = ({ category, status }: CategorizedDraftError, failedAttempt: number) => {
    onEvent({ type: 'save-failure', attempt: failedAttempt, category, status });

    if (category === 'offline' || !isBrowserOnline()) {
      // Wait for connectivity; do not consume the retry budget.
      setState({ status: 'offline' });
      return;
    }

    if (!isRetryableDraftError(category)) {
      stopped = true;
      clearAllTimers();
      onEvent({ type: 'stopped', category });
      setState({ status: 'unavailable' });
      return;
    }

    if (failedAttempt >= retryDelaysMs.length) {
      onEvent({ type: 'paused' });
      setState({ status: 'paused' });
      return;
    }

    const baseDelay = retryDelaysMs[failedAttempt];
    const delayMs = Math.round(baseDelay * (1 + Math.random() * jitterRatio));
    attempt = failedAttempt + 1;
    onEvent({ type: 'retry-scheduled', attempt: attempt, delayMs });
    setState({ status: 'retrying' });
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      if (canSave() && !inFlight) void runSave();
    }, delayMs);
  };

  /** Saves the latest snapshot now if it differs from the acknowledged baseline. */
  function flush() {
    clearScheduling();
    if (!canSave() || inFlight || retryTimer !== undefined) return;
    if (state.status === 'paused') return;
    if (!hasUnsavedChanges()) {
      if (state.status === 'dirty') setState({ status: settledStatus() });
      return;
    }
    if (!isBrowserOnline()) {
      setState({ status: 'offline' });
      return;
    }
    void runSave();
  }

  const unsubscribeConnectivity = subscribeConnectivity((online) => {
    if (!canSave()) return;
    if (!online) {
      if (hasUnsavedChanges() || inFlight) setState({ status: 'offline' });
      return;
    }
    if (state.status === 'offline') {
      attempt = 0;
      if (hasUnsavedChanges()) flush();
      else setState({ status: settledStatus() });
    }
  });

  return {
    getState: () => state,

    subscribe: (listener: (next: AutosaveState) => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    /**
     * Marks `snapshot` as already persisted (hydration/restore/autofill) without saving it (spec FR-07).
     * Also resets the lifecycle after a discard.
     */
    setBaseline: (snapshot: DraftSnapshot) => {
      if (disposed) return;
      generation += 1;
      clearAllTimers();
      inFlight = false;
      attempt = 0;
      stopped = false;
      baseline = snapshot;
      latest = snapshot;
      state = { status: 'idle' };
      setState({});
    },

    /** Records the current editing state; saves are scheduled only for meaningful answer changes. */
    notifyChange: (snapshot: DraftSnapshot) => {
      if (disposed) return;
      latest = snapshot;
      if (!canSave()) return;

      if (!hasUnsavedChanges()) {
        // The edit was reverted to the saved state.
        if (state.status === 'dirty') {
          clearScheduling();
          setState({ status: settledStatus() });
        }
        return;
      }

      // These states already convey that recent changes aren't saved; the pending work picks up `latest`.
      if (inFlight || retryTimer !== undefined || ['offline', 'paused'].includes(state.status)) return;

      setState({ status: 'dirty' });
      scheduleSave();
    },

    /** Saves pending changes immediately (e.g. on page hide). */
    flush,

    /** Manual retry after the automatic budget is exhausted. */
    retryNow: () => {
      if (!canSave() || inFlight) return;
      clearTimer(retryTimer);
      retryTimer = undefined;
      attempt = 0;
      if (state.status === 'paused') setState({ status: 'dirty' });
      flush();
    },

    /** Suspends autosave during submit. Responses from in-flight saves are ignored. */
    suspend: () => {
      if (disposed) return;
      generation += 1;
      suspended = true;
      inFlight = false;
      clearAllTimers();
      setState({ status: 'submitting' });
    },

    /** Resumes after a failed submit; unsaved changes are scheduled again. */
    resume: () => {
      if (disposed || !suspended) return;
      suspended = false;
      attempt = 0;
      if (stopped) return;
      if (hasUnsavedChanges()) {
        setState({ status: 'dirty' });
        scheduleSave();
      } else {
        setState({ status: settledStatus() });
      }
    },

    hasUnsavedChanges: () => hasUnsavedChanges() || inFlight,

    dispose: () => {
      if (disposed) return;
      disposed = true;
      generation += 1;
      clearAllTimers();
      unsubscribeConnectivity();
      listeners.clear();
    },
  };
};

export type AutosaveCoordinator = ReturnType<typeof createAutosaveCoordinator>;
